/**
 * Notifications.
 *
 * Everything here is scheduled *on the device*. There is no push
 * infrastructure, no Firebase project, no service-account key on the server —
 * and consequently nothing to break when the home connection drops. A rest
 * timer and a workout reminder are both things the phone already knows enough
 * to fire by itself.
 *
 * Two Android facts shape this file:
 *
 *  - **Android 13+ requires runtime POST_NOTIFICATIONS.** It is requested at
 *    the moment the user does something that implies wanting a notification,
 *    never at first launch — a permission dialog on launch gets reflexively
 *    denied, and denial is permanent-ish.
 *
 *  - **A notification cannot turn a display on.** The rest timer's own alarm
 *    is therefore NOT scheduled through the notifications plugin on Android —
 *    it goes through RestAlarm, a small native plugin that posts the same
 *    notification with a full-screen intent attached, which is the one
 *    mechanism the system will wake a screen for. The plugin has no API for it
 *    (its Android sources contain no mention of fullScreenIntent), so this is
 *    the reason a native plugin exists rather than a flag being passed. Every
 *    other notification here — the workout reminders — still goes through the
 *    plugin, because none of them should ever wake anybody.
 *
 *  - **Android 12+ restricts exact alarms**, and this app now asks for them —
 *    `USE_EXACT_ALARM` in deploy/patch_android.py. It used to skip them on the
 *    grounds that the foreground JS timer was the real mechanism and ±15 min
 *    was fine. That reasoning covered the reminders and quietly failed the rest
 *    timer: without the permission the plugin downgrades to an inexact alarm,
 *    measured at ~31s late on Android 14. `USE_EXACT_ALARM` is granted at
 *    install with no prompt, so it costs the user nothing.
 */

import { registerPlugin } from '@capacitor/core'

import { withTimeoutOr } from '../lib/timeout'
import { loadPlugin, platform } from './platform'

// Separate channels so the user can silence reminders without losing the rest
// timer, which is the one that actually matters mid-workout.
const CHANNEL_REST = 'rest-timer'
const CHANNEL_REMINDER = 'workout-reminders'

// Notification ids are ints. Reserving disjoint ranges means cancelling all
// reminders can't accidentally cancel a running rest timer.
const REST_ID = 1
const REMINDER_ID_BASE = 1000

async function notifications() {
  return loadPlugin(() => import('@capacitor/local-notifications'), 'LocalNotifications')
}

type RestAlarmPlugin = {
  schedule(options: { seconds: number }): Promise<{ scheduled: boolean; exact: boolean }>
  cancel(): Promise<{ cancelled: boolean }>
  canWakeScreen(): Promise<{ allowed: boolean; exact: boolean }>
  openWakeSettings(): Promise<{ opened: boolean }>
  getTone(): Promise<{ enabled: boolean; headphones: boolean }>
  setTone(options: { enabled: boolean }): Promise<{ enabled: boolean }>
  testTone(): Promise<{ played: boolean; headphones: boolean }>
}

/**
 * Registered statically, like Installer in native/update.ts, and for the same
 * reason: `registerPlugin` returns a proxy synchronously, so there is never a
 * promise wrapping a plugin object. That shape is what hung every plugin in
 * this app for its whole life — see lib/unthenable. Awaiting a *method call* on
 * it is fine; awaiting the object is not, and this way no call site can.
 */
const RestAlarm = registerPlugin<RestAlarmPlugin>('RestAlarm')

/** Android only. Elsewhere the plugin proxy would reject with "not implemented". */
function hasRestAlarm(): boolean {
  return platform() === 'android'
}

export type RestAlertStatus = {
  /** Android will let the alert turn the screen on. */
  allowed: boolean
  /** The alarm under it is exact, not the ~15-minute-window kind. */
  exact: boolean
}

/**
 * Whether the rest alert can actually wake the phone, asked fresh.
 *
 * Never cached: the answer changes the moment the user flips the toggle in
 * Settings, and a cached "no" is what made the update button bounce back to
 * Settings forever the last time this app cached a permission.
 *
 * `null` off Android, where there is no such thing to report.
 */
export async function restAlertStatus(): Promise<RestAlertStatus | null> {
  if (!hasRestAlarm()) return null
  return withTimeoutOr(
    RestAlarm.canWakeScreen().then((r) => ({ allowed: r.allowed, exact: r.exact })),
    4000,
    null,
    'RestAlarm.canWakeScreen',
  )
}

/** Send the user to the Settings page that grants it. */
export async function openRestAlertSettings(): Promise<boolean> {
  if (!hasRestAlarm()) return false
  return withTimeoutOr(
    RestAlarm.openWakeSettings().then((r) => r.opened),
    4000,
    false,
    'RestAlarm.openWakeSettings',
  )
}

export type RestToneStatus = {
  /** The tone is switched on in Settings. */
  enabled: boolean
  /** Headphones are connected right now — the only time the alarm plays it. */
  headphones: boolean
}

/**
 * The headphone tone at the end of a rest, as the phone has it.
 *
 * `null` off Android, and on an APK built before the tone existed — there the
 * method isn't implemented, the call rejects, and Settings must show nothing
 * rather than a switch wired to nothing.
 */
export async function restToneStatus(): Promise<RestToneStatus | null> {
  if (!hasRestAlarm()) return null
  return withTimeoutOr(
    RestAlarm.getTone().then((r) => ({ enabled: r.enabled, headphones: r.headphones })),
    4000,
    null,
    'RestAlarm.getTone',
  )
}

/** Switch it on or off. Resolves to the saved value, or null on failure. */
export async function setRestTone(enabled: boolean): Promise<boolean | null> {
  if (!hasRestAlarm()) return null
  return withTimeoutOr(
    RestAlarm.setTone({ enabled }).then((r) => r.enabled),
    4000,
    null,
    'RestAlarm.setTone',
  )
}

/** Play it once now. Resolves after it has finished, or false on failure. */
export async function testRestTone(): Promise<boolean> {
  if (!hasRestAlarm()) return false
  return withTimeoutOr(
    RestAlarm.testTone().then((r) => r.played),
    5000,
    false,
    'RestAlarm.testTone',
  )
}

let channelsReady = false

async function ensureChannels(): Promise<void> {
  if (channelsReady) return
  const ln = await notifications()
  if (!ln) return
  try {
    await ln.createChannel({
      id: CHANNEL_REST,
      name: 'Rest timer',
      description: 'Tells you when your rest between sets is up.',
      importance: 5,          // pops as a heads-up — the whole point
      vibration: true,
    })
    await ln.createChannel({
      id: CHANNEL_REMINDER,
      name: 'Workout reminders',
      description: 'Reminds you about a scheduled workout.',
      importance: 3,          // quiet; this one shouldn't interrupt
      vibration: false,
    })
    channelsReady = true
  } catch (err) {
    console.warn('[notify] could not create channels:', err)
  }
}

/**
 * Ask for permission, in context.
 *
 * Returns false on denial, and callers must degrade rather than throw: a user
 * who says no to notifications should still get a fully working rest timer,
 * just without the backgrounded-app backup.
 */
export async function requestPermission(): Promise<boolean> {
  const ln = await notifications()
  if (!ln) {
    // Web fallback.
    if (typeof Notification === 'undefined') return false
    if (Notification.permission === 'granted') return true
    if (Notification.permission === 'denied') return false
    try {
      return (await Notification.requestPermission()) === 'granted'
    } catch {
      return false
    }
  }

  try {
    const current = await ln.checkPermissions()
    if (current.display === 'granted') return true
    const asked = await ln.requestPermissions()
    return asked.display === 'granted'
  } catch (err) {
    console.warn('[notify] permission request failed:', err)
    return false
  }
}

export async function hasPermission(): Promise<boolean> {
  const ln = await notifications()
  if (!ln) {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted'
  }
  try {
    return (await ln.checkPermissions()).display === 'granted'
  } catch {
    return false
  }
}

/** Fire when the rest period ends. Cancelled if the user starts the next set early. */
export async function scheduleRestDone(seconds: number): Promise<void> {
  if (hasRestAlarm()) {
    // Permission still gates it: POST_NOTIFICATIONS denied means the native
    // side would schedule an alarm whose notification the system drops on the
    // floor, and the prompt belongs at the moment someone starts a rest.
    if (!(await hasPermission())) return

    const scheduled = await withTimeoutOr(
      RestAlarm.schedule({ seconds }).then((r) => r.scheduled),
      4000,
      false,
      'RestAlarm.schedule',
    )
    if (scheduled) {
      // Belt and braces for the upgrade: a build before this one may have left
      // a rest notification pending in the notifications plugin, and two alarms
      // for one rest is worse than either.
      const stale = await notifications()
      if (stale) {
        try {
          await stale.cancel({ notifications: [{ id: REST_ID }] })
        } catch {
          /* nothing of the plugin's was pending */
        }
      }
      return
    }
    // Fell through: the native call failed or timed out. The plugin path below
    // still gets you a notification on time — it just won't light the screen,
    // which is better than a rest timer that says nothing at all.
    console.warn('[notify] RestAlarm unavailable, falling back to the plugin')
  }

  const ln = await notifications()
  if (!ln) return
  if (!(await hasPermission())) return

  await ensureChannels()
  try {
    await ln.cancel({ notifications: [{ id: REST_ID }] })
    await ln.schedule({
      notifications: [{
        id: REST_ID,
        channelId: CHANNEL_REST,
        title: 'Rest over',
        body: 'Next set.',
        // allowWhileIdle pierces Doze. With USE_EXACT_ALARM held it maps to
        // setExactAndAllowWhileIdle; without it the plugin silently falls back
        // to setAndAllowWhileIdle, which still survives Doze but is inexact —
        // ~31s late, measured. Both halves matter: allowWhileIdle for the Doze
        // window, the permission for the timing inside it. The 9-minute
        // per-app throttle is irrelevant here: rest timers are one at a time,
        // and the app is in the active bucket.
        schedule: { at: new Date(Date.now() + seconds * 1000), allowWhileIdle: true },
      }],
    })
  } catch (err) {
    console.warn('[notify] could not schedule rest timer:', err)
  }
}

export async function cancelRestDone(): Promise<void> {
  // Both paths, unconditionally. Which one armed this rest depends on when the
  // app was last updated and whether a native call timed out; a Skip that
  // cancels only the one it assumes is there leaves the other to fire two
  // minutes later at somebody already mid-set.
  if (hasRestAlarm()) {
    await withTimeoutOr(
      RestAlarm.cancel().then(() => true),
      4000,
      false,
      'RestAlarm.cancel',
    )
  }

  const ln = await notifications()
  if (!ln) return
  try {
    await ln.cancel({ notifications: [{ id: REST_ID }] })
  } catch {
    /* nothing scheduled */
  }
}

export type Reminder = { at: Date; title: string; body: string }

/**
 * Replace all scheduled workout reminders.
 *
 * Call on every app open, not just when the plan changes. Android clears
 * pending alarms on force-stop and on some OEM battery optimisations, so
 * "schedule once when the plan is created" quietly stops working after the
 * first time the user swipes the app away. Re-scheduling a fortnight's worth
 * each launch is cheap and self-healing.
 */
export async function rescheduleReminders(reminders: Reminder[]): Promise<void> {
  const ln = await notifications()
  if (!ln) return
  if (!(await hasPermission())) return

  await ensureChannels()
  try {
    const pending = await ln.getPending()
    const stale = pending.notifications.filter((n) => n.id >= REMINDER_ID_BASE)
    if (stale.length > 0) await ln.cancel({ notifications: stale })

    const future = reminders.filter((r) => r.at.getTime() > Date.now())
    if (future.length === 0) return

    await ln.schedule({
      notifications: future.slice(0, 32).map((r, i) => ({
        id: REMINDER_ID_BASE + i,
        channelId: CHANNEL_REMINDER,
        title: r.title,
        body: r.body,
        schedule: { at: r.at, allowWhileIdle: false },
      })),
    })
  } catch (err) {
    console.warn('[notify] could not schedule reminders:', err)
  }
}
